import type { GraphicsProfile, RenderMode } from '../core/types';
import type { RendererFactory } from './renderer';
import { createCanvas2dRenderer } from './canvas2d/canvas2d-renderer';
import { createWebglRenderer } from './webgl/webgl-renderer';

/**
 * Effective-mode resolution (AD-3, Story 1.6). A PURE function of the requested
 * profile and the feature-detected capabilities, living in `render` so `state`
 * never imports `platform` and the fallback is unit-testable headless (AD-11).
 *
 * Canvas2D is the COMPLETE baseline and the Compatibility path (AD-3); the WebGL
 * adapter adds degradable decoration only (AD-9). Selection is feature-detection
 * only, never browser-name: this function parses no user-agent string and reads
 * only `capabilities.webgl`.
 *
 * Rules:
 *   - `'compatibility'` always resolves to the Canvas2D factory.
 *   - `'enhanced'` resolves to the WebGL factory only when WebGL is available,
 *     otherwise it falls back to Canvas2D and reports the missing features.
 *   - `'auto'` selects the safest suitable profile: Enhanced when WebGL is
 *     available, else Compatibility.
 */

/**
 * The render-local capability shape (declared in `render`, not imported from
 * `platform`, to respect the dependency table). The composition root passes a
 * compatible object built from `platform` detection.
 */
export interface RenderCapabilities {
  readonly webgl: boolean;
}

/** The degradable WebGL decoration features, listed as human-readable names. */
export const ENHANCED_FEATURES: readonly string[] = [
  'WebGL-assisted particles',
  'Soft shadows',
  'Dynamic lighting',
  'Higher-resolution effects'
];

export interface ResolvedProfile {
  /** The factory for the adapter that should mount for the active mode. */
  readonly createRenderer: RendererFactory;
  /** The concrete active render mode this request resolves to. */
  readonly activeMode: RenderMode;
  /**
   * Enhanced decoration features unavailable on this device. Device-capability
   * honest: populated exactly when WebGL is absent (so it is non-empty on any
   * fallback from Enhanced, and empty whenever WebGL is present).
   */
  readonly unavailableFeatures: string[];
}

export function resolveProfile(
  requestedProfile: GraphicsProfile,
  capabilities: RenderCapabilities
): ResolvedProfile {
  const webgl = capabilities.webgl === true;
  // Device-capability honest: the enhanced decoration features are unavailable
  // exactly when there is no WebGL context to run them on, independent of the
  // requested profile. This makes any Enhanced fallback report a non-empty list.
  const unavailableFeatures = webgl ? [] : [...ENHANCED_FEATURES];

  // Compatibility is always the Canvas2D baseline. Auto and Enhanced upgrade to
  // the WebGL decoration adapter only when the capability is present.
  const wantsEnhanced = requestedProfile === 'enhanced' || requestedProfile === 'auto';
  const activeMode: RenderMode = wantsEnhanced && webgl ? 'enhanced' : 'compatibility';
  const createRenderer: RendererFactory =
    activeMode === 'enhanced' ? createWebglRenderer : createCanvas2dRenderer;

  return { createRenderer, activeMode, unavailableFeatures };
}
